import { actions } from './reducer'
import {
  selectActiveAdminHsSitesWithDwb,
  selectActiveAdminK8SitesWithDwb,
  selectActiveAdminStandaloneDwbSites,
} from 'store/licenseManager/selectors'
import api from '../../lib/api/reportsApi'
import {
  addMissingSiteIdToNullSiteDetailsResponse,
  getLessonCompletionCacheKey,
  hasLessonCompletionCacheEntry,
  resolveLessonCompletionApiProgram,
} from './lessonCompletionHelpers'
import { formatError } from 'utils/formatError'
import { backoff } from '@cfc/exponential-backoff'

const clearErrors = () => {
  return async dispatch => {
    dispatch(actions.clearErrors())
  }
}

const fetchHsAdminSnapshot = adminEmail => {
  return async dispatch => {
    dispatch(actions.fetchingHsLaunchSnapshot())
    try {
      const { data } = await api.getAdminHsLaunchSnapshot(adminEmail)
      validateResponse(data)
      dispatch(actions.fetchHsAdminSnapshotSuccess(data))
    } catch (error) {
      dispatch(
        actions.fetchHsAdminSnapshotError({
          error: formatError(error),
        }),
      )
    }
  }
}

const fetchK8PlaybookData = (adminEmail, siteId) => {
  return async dispatch => {
    dispatch(actions.fetchingK8PlaybookData())
    try {
      const { data } = await api.getK8PlaybookData(adminEmail, siteId)
      validateResponse(data)
      dispatch(actions.fetchK8PlaybookDataSuccess(data))
    } catch (error) {
      dispatch(actions.fetchK8PlaybookDataError({ error: formatError(error) }))
    }
  }
}

const fetchTier2DashboardData = ({ adminEmail, program }) => {
  return async dispatch => {
    dispatch(actions.fetchingTier2DashboardData())
    try {
      const { data } = await backoff(
        () => api.getTier2DashboardData(adminEmail, program),
        3,
      )
      validateResponse(data)
      dispatch(actions.fetchTier2DashboardDataSuccess(data))
    } catch (error) {
      dispatch(
        actions.fetchTier2DashboardDataError({
          error: formatError(error),
        }),
      )
    }
  }
}

const fetchTier2LessonCompletionData = ({ siteId, program, gradeBand }) => {
  return async dispatch => {
    dispatch(actions.fetchingTier2LessonCompletionData({ siteId, gradeBand }))
    try {
      const { data } = await backoff(
        () => api.getTier2LessonCompletionData(siteId, program, gradeBand),
        3,
      )
      validateResponse(data)
      dispatch(
        actions.fetchTier2LessonCompletionDataSuccess({
          ...data,
          siteId,
          gradeBand,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchTier2LessonCompletionDataError({
          error: formatError(error),
          siteId,
          gradeBand,
        }),
      )
    }
  }
}

const fetchHsUserProgressSnapshot = (siteId, program, practiceType) => {
  return async dispatch => {
    dispatch(actions.fetchingHsUserProgressSnapshot())
    try {
      const { data } = await api.getHsUserProgressSnapshot(
        siteId,
        program,
        practiceType,
      )
      validateResponse(data)
      dispatch(
        actions.fetchHsUserProgressSnapshotSuccess({
          data: data.data,
          siteId,
          practiceType,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchHsUserProgressSnapshotError({ error: formatError(error) }),
      )
    }
  }
}

const fetchLessonCompletionData = ({
  adminEmail,
  siteId,
  program,
  toggleView,
  k8ToggleView,
}) => {
  return async (dispatch, getState) => {
    const resolvedToggleView = toggleView ?? k8ToggleView

    const resolveDwbSiteIds = state => {
      if (program === 'DWB') {
        const standaloneSites = selectActiveAdminStandaloneDwbSites(state)
        return Array.isArray(standaloneSites)
          ? standaloneSites.map(site => site.id)
          : []
      }

      const standaloneSites = selectActiveAdminStandaloneDwbSites(state) ?? []
      const programSitesWithDwb =
        program === 'HS'
          ? selectActiveAdminHsSitesWithDwb(state) ?? []
          : selectActiveAdminK8SitesWithDwb(state) ?? []

      return [...programSitesWithDwb, ...standaloneSites].map(site => site.id)
    }

    const apiProgram = resolveLessonCompletionApiProgram(program)

    const buildCacheKey = state =>
      getLessonCompletionCacheKey({
        toggleView: resolvedToggleView,
        program,
        siteId,
        dwbSiteIds: resolveDwbSiteIds(state),
      })

    const cacheKey = buildCacheKey(getState())
    const { lessonCompletionByView } = getState().reportsManager.admin

    if (hasLessonCompletionCacheEntry(lessonCompletionByView, cacheKey)) {
      dispatch(actions.restoreLessonCompletionFromCache({ cacheKey }))
      return
    }

    dispatch(
      actions.fetchingLessonCompletionData(adminEmail, siteId, apiProgram),
    )
    try {
      const { data } = await api.getLessonCompletionData(
        adminEmail,
        siteId,
        apiProgram,
        resolvedToggleView,
      )
      validateResponse(data)
      const dwbSiteIds = resolveDwbSiteIds(getState())
      dispatch(
        actions.fetchLessonCompletionDataSuccess({
          cacheKey,
          data,
          dwbSiteIds,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchLessonCompletionDataError({
          error: formatError(error),
        }),
      )
    }
  }
}

const fetchLessonCompletionDataForDWB = ({ adminEmail, siteId, program }) => {
  return async (dispatch, getState) => {
    const apiProgram = resolveLessonCompletionApiProgram(program)
    const { lessonCompletionDwbBySite } = getState().reportsManager.admin

    if (lessonCompletionDwbBySite?.[siteId]?.siteDetails) {
      dispatch(actions.restoreLessonCompletionDwbFromCache())
      return
    }

    dispatch(
      actions.fetchingLessonCompletionData(adminEmail, siteId, apiProgram),
    )
    try {
      const { data } = await api.getLessonCompletionDataForDWB(siteId)
      validateResponse(data)
      dispatch(
        actions.fetchLessonCompletionDwbDataSuccess({
          siteId,
          data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchLessonCompletionDataError({
          error: formatError(error),
        }),
      )
    }
  }
}

const validateResponse = data => {
  const received200ButErrorObjectExists = data.errors && data.errors.length > 0

  if (received200ButErrorObjectExists) {
    const graphqlError = data.errors[0] ?? {}
    const { message, extensions } = graphqlError
    const error = new Error(message)

    if (extensions?.statusCode != null) {
      error.statusCode = extensions.statusCode
    }
    if (extensions?.internalMessage) {
      error.internalMessage = extensions.internalMessage
    }

    throw error
  }
}

const deleteReportsData = () => {
  return async dispatch => {
    dispatch(actions.deletingReportsData())
  }
}

const fetchK8ReportSchoolBySiteId = siteId => {
  return async dispatch => {
    dispatch(actions.fetchingReportsSchoolData())
    try {
      const { data } = await api.getK8ReportSchoolBySiteId(siteId)
      validateResponse(data)
      const successPayload = addMissingSiteIdToNullSiteDetailsResponse(
        data,
        siteId,
      )
      dispatch(actions.fetchK8ReportSchoolSuccess(successPayload))
    } catch (error) {
      dispatch(
        actions.fetchingReportsSchoolDataError({ error: formatError(error) }),
      )
    }
  }
}

const fetchAdminK8Sites = (pageSize, pageNumber, orderBy, sortKey) => {
  return async dispatch => {
    dispatch(actions.fetchingAdminK8SitesList())

    try {
      const { data } = await api.getAdminK8SitesPaginated(
        pageSize,
        pageNumber,
        orderBy,
        sortKey,
      )
      validateResponse(data)

      const payload = data

      dispatch(actions.fetchAdminK8SitesSuccess(payload))
    } catch (error) {
      dispatch(
        actions.fetchingAdminK8SitesListError({ error: formatError(error) }),
      )
    }
  }
}

const fetchAdminK8SitesSummary = () => {
  return async dispatch => {
    dispatch(actions.fetchingAdminK8SitesSummary())

    try {
      const { data } = await api.getAdminK8SitesSummary()

      validateResponse(data)
      dispatch(actions.fetchAdminK8SitesSummarySuccess(data))
    } catch (error) {
      dispatch(
        actions.fetchingAdminK8SitesSummaryError({ error: formatError(error) }),
      )
    }
  }
}

const fetchAdminSelaSitesList = () => {
  return async dispatch => {
    dispatch(actions.fetchingAdminSelaSitesList())

    try {
      const { data } = await api.getAdminSelaSitesList()
      validateResponse(data)

      const payload = data

      dispatch(actions.fetchAdminSelaSitesListSuccess(payload))
    } catch (error) {
      dispatch(
        actions.fetchingAdminSelaSitesListError({ error: formatError(error) }),
      )
    }
  }
}

const fetchK8ReportGradeBySiteId = (siteId, gradeId) => {
  return async dispatch => {
    dispatch(actions.fetchingReportsGradeData())
    try {
      const { data } = await api.getK8ReportGradeBySiteId(siteId, gradeId)
      validateResponse(data)
      const successPayload = addMissingSiteIdToNullSiteDetailsResponse(
        data,
        siteId,
      )
      dispatch(actions.fetchK8ReportGradeSuccess(successPayload))
    } catch (error) {
      dispatch(
        actions.fetchK8ReportGradeError({
          error: formatError(error),
        }),
      )
    }
  }
}

const fetchK8ReportGradeAndClassDetailsBySiteId = (
  siteId,
  gradeId,
  classInstance,
) => {
  return async dispatch => {
    dispatch(actions.fetchingReportsGradeAndClassData())
    try {
      const { data } = await api.getK8ReportGradeAndClassDetailsBySiteId(
        siteId,
        gradeId,
        classInstance,
      )
      validateResponse(data)
      const successPayload = addMissingSiteIdToNullSiteDetailsResponse(
        data,
        siteId,
      )
      dispatch(actions.fetchK8ReportGradeAndClassDetailsSuccess(successPayload))
    } catch (error) {
      dispatch(
        actions.fetchK8ReportGradeAndClassDetailsError({
          error: formatError(error),
        }),
      )
    }
  }
}

const fetchK8ReportClassDetailsBySiteId = (siteId, classInstance) => {
  return async dispatch => {
    dispatch(actions.fetchingReportsClassData())
    try {
      const { data } = await api.getK8ReportClassDetailsBySiteId(
        siteId,
        classInstance,
      )

      validateResponse(data)
      const successPayload = addMissingSiteIdToNullSiteDetailsResponse(
        data,
        siteId,
      )
      dispatch(actions.fetchK8ReportClassDetailsSuccess(successPayload))
    } catch (error) {
      dispatch(
        actions.fetchingK8ReportClassDetailsError({
          error: formatError(error, false, 'k8-class-details'),
        }),
      )
    }
  }
}

const fetchReportsContentTrack = licenseId => {
  return async dispatch => {
    dispatch(actions.fetchingReportsData())
    try {
      const { data } = await api.getReportsContentTrack(licenseId)
      validateResponse(data)
      dispatch(actions.fetchingReportsContentTrackSuccess(data))
    } catch (error) {
      dispatch(actions.fetchingReportsDataError({ error: formatError(error) }))
    }
  }
}

const fetchReportsUserDetails = licenseId => {
  return async (dispatch, store) => {
    const userDetailsExist =
      store?.reportsManager?.reports?.[licenseId]?.user_details
    dispatch(actions.fetchingReportsData())

    try {
      if (!userDetailsExist) {
        const { data } = await api.getReportsUsersDetail(licenseId)
        validateResponse(data)
        dispatch(actions.fetchingReportsUsersDetailSuccess(data))
      }
    } catch (error) {
      dispatch(actions.fetchingReportsDataError({ error: formatError(error) }))
    }
  }
}

const resetSelectedProgram = () => {
  return async dispatch => {
    dispatch(actions.resetSelectedProgram())
  }
}

const updateHsUserProgressPaginationSortKey = sortKey => {
  return dispatch => {
    dispatch(actions.updateHsUserProgressPaginationSortKey({ sortKey }))
  }
}

const updateHsUserProgressPaginationSortOrder = sortOrder => {
  return dispatch => {
    dispatch(actions.updateHsUserProgressPaginationSortOrder({ sortOrder }))
  }
}

const updateSelectedContentTrack = contentTrack => {
  return dispatch => {
    dispatch(actions.updateSelectedContentTrack({ contentTrack }))
  }
}

const updateSelectedGrade = gradeName => {
  return dispatch => {
    dispatch(actions.updateSelectedGrade({ gradeName }))
  }
}

const updateSelectedLicense = licenseId => {
  return dispatch => {
    dispatch(actions.updateSelectedLicense({ licenseId }))
  }
}

const updateSelectedModuleId = moduleContentfulId => {
  return dispatch => {
    dispatch(actions.updateSelectedModuleId({ moduleContentfulId }))
  }
}
const updateLaunchSelectedPaginationSortKey = sortKey => {
  return dispatch => {
    dispatch(actions.updateLaunchSelectedPaginationSortKey({ sortKey }))
  }
}
const updateLaunchSelectedPaginationSortOrder = orderBy => {
  return dispatch => {
    dispatch(actions.updateLaunchSelectedPaginationSortOrder({ orderBy }))
  }
}
const updateMonitorSelectedPaginationCurrentPage = page => {
  return dispatch => {
    dispatch(actions.updateMonitorSelectedPaginationCurrentPage({ page }))
  }
}
const updateMonitorSelectedPaginationSortKey = sortKey => {
  return dispatch => {
    dispatch(actions.updateMonitorSelectedPaginationSortKey({ sortKey }))
  }
}
const updateMonitorSelectedPaginationSortOrder = orderBy => {
  return dispatch => {
    dispatch(actions.updateMonitorSelectedPaginationSortOrder({ orderBy }))
  }
}
const updateDwbMonitorSelectedPaginationCurrentPage = page => {
  return dispatch => {
    dispatch(actions.updateDwbMonitorSelectedPaginationCurrentPage({ page }))
  }
}
const updateDwbMonitorSelectedPaginationSortKey = sortKey => {
  return dispatch => {
    dispatch(actions.updateDwbMonitorSelectedPaginationSortKey({ sortKey }))
  }
}
const updateDwbMonitorSelectedPaginationSortOrder = orderBy => {
  return dispatch => {
    dispatch(actions.updateDwbMonitorSelectedPaginationSortOrder({ orderBy }))
  }
}
const updateSelectedSite = site => {
  return dispatch => {
    dispatch(actions.updateSelectedSite({ site }))
  }
}
const updateProgramTitles = programTitles => {
  return dispatch => {
    dispatch(actions.updateProgramTitles({ programTitles }))
  }
}
const updateProgramsForUser = programs => {
  return dispatch => {
    dispatch(actions.updateProgramsForUser({ programs }))
  }
}
const updateSelectedProgram = program => {
  return dispatch => {
    dispatch(actions.updateSelectedProgram({ program }))
  }
}

export default {
  clearErrors,
  deleteReportsData,
  fetchHsAdminSnapshot,
  fetchLessonCompletionData,
  fetchLessonCompletionDataForDWB,
  fetchReportsContentTrack,
  fetchReportsUserDetails,
  fetchK8PlaybookData,
  fetchK8ReportSchoolBySiteId,
  fetchK8ReportGradeBySiteId,
  fetchK8ReportGradeAndClassDetailsBySiteId,
  fetchK8ReportClassDetailsBySiteId,
  fetchAdminK8Sites,
  fetchAdminK8SitesSummary,
  fetchAdminSelaSitesList,
  fetchHsUserProgressSnapshot,
  fetchTier2DashboardData,
  fetchTier2LessonCompletionData,
  resetSelectedProgram,
  updateHsUserProgressPaginationSortKey,
  updateHsUserProgressPaginationSortOrder,
  updateProgramTitles,
  updateProgramsForUser,
  updateSelectedContentTrack,
  updateSelectedGrade,
  updateSelectedLicense,
  updateSelectedModuleId,
  updateLaunchSelectedPaginationSortKey,
  updateLaunchSelectedPaginationSortOrder,
  updateMonitorSelectedPaginationCurrentPage,
  updateMonitorSelectedPaginationSortKey,
  updateMonitorSelectedPaginationSortOrder,
  updateDwbMonitorSelectedPaginationCurrentPage,
  updateDwbMonitorSelectedPaginationSortKey,
  updateDwbMonitorSelectedPaginationSortOrder,
  updateSelectedProgram,
  updateSelectedSite,
}
